import { authModule } from "@/src/auth/auth";

import wretch, { FetchLike, WretchError, WretchOptions } from "wretch";

// @ts-ignore
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
// @ts-ignore
const PUBLIC_API_BASE_URL = import.meta.env.VITE_PUBLIC_API_BASE_URL;
// @ts-ignore
const ADMIN_API_BASE_URL = import.meta.env.VITE_ADMIN_API_BASE_URL;

/**
 * Every api error is mapped to this error type
 */
export class ApiError<ErrorResponse> extends Error {
  response: ErrorResponse;
  requestId: string;
  status: number;
  constructor(message: string, response: ErrorResponse, status: number) {
    super(message);
    this.name = "ApiError";
    this.response = response;
    this.status = status;
  }
}

/**
 * Decodes error response body JSON into a custom ApiError object
 */
const errorMapper = (err: WretchError) => {
  let responseJSON = null;
  try {
    responseJSON = JSON.parse(err.message);
  } catch (e) {
    console.error("Error response body JSON parsing failed");
  }

  throw new ApiError(err.message, responseJSON, err.status);
};

const authMiddleware =
  (next: FetchLike) => async (url: string, options: WretchOptions) => {
    // Get id token from local storage
    const token = await authModule.getIdToken();

    // Attach the token to the Authorization header
    options.headers = {
      ...options.headers,
      Authorization: `${token}`
    };

    const response = await next(url, options);
    return response;
  };

export const api = wretch(API_BASE_URL)
  .middlewares([authMiddleware])
  .catcher(400, errorMapper)
  .catcher(401, errorMapper)
  .catcher(403, errorMapper)
  .catcher(404, errorMapper)
  .catcher(405, errorMapper)
  .catcher(415, errorMapper)
  .catcher(422, errorMapper)
  .catcher(429, errorMapper)
  .catcher(500, errorMapper);

export const publicApi = wretch(PUBLIC_API_BASE_URL)
  .catcher(400, errorMapper)
  .catcher(401, errorMapper)
  .catcher(403, errorMapper)
  .catcher(404, errorMapper)
  .catcher(405, errorMapper)
  .catcher(415, errorMapper)
  .catcher(422, errorMapper)
  .catcher(429, errorMapper)
  .catcher(500, errorMapper);

export const adminApi = wretch(ADMIN_API_BASE_URL)
  .middlewares([authMiddleware])
  .catcher(400, errorMapper)
  .catcher(401, errorMapper)
  .catcher(403, errorMapper)
  .catcher(404, errorMapper)
  .catcher(405, errorMapper)
  .catcher(409, errorMapper)
  .catcher(415, errorMapper)
  .catcher(422, errorMapper)
  .catcher(429, errorMapper)
  .catcher(500, errorMapper);

export enum apiPaths {
  AUCTIONS = "/auctions",
  AUCTIONITEMS = "/auctionItems",
  ADMINS_AUCTIONS = "/admins/auctions",
  ADMINS_BUSINESSES = "/admins/businesses",
  ADMINS_USERS = "/admins/users",
  ADMINS_ANNOUNCEMENTS = "/admins/announcements",
  ADMINS_AUCTION_ITEMS = "/admins/auctionItems",
  ADMINS_PATCH_AND_INFO = "/admins/patchAndInfo",
  ADMINS_VERTICAL_CHECKS = "/admins/vertical-checks",
  ADMINS_ADMIN_NOTES = "/admins/adminNotes",
  ADMINS_BILLING_REPORT_DOWNLOAD = "/admins/billing-report/download",
  BIDS = "/bids",
  BIDMACHINES = "/bidMachines",
  USER_DETAILS = "/user/details",
  SETTINGS = "/users",
  NOTIFICATIONS = "/notifications",
  AUCTIONS_SUMMARY = "/auctionsSummary",
  PATCH_AND_INFO = "/patchAndInfo",
  USER_ACTIONS = "/userActions",
  FEEDBACK = "/feedback"
}
